import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import { Plus, CalendarRange, X, Check, AlertTriangle, FileText, ExternalLink, Lock } from "lucide-react";
import CommentThread from "../components/CommentThread.jsx";

const STATUS_COLORS = {
  Active: "bg-[#D4EDDA] text-[#155724]",
  Expired: "bg-[#FFF3CD] text-[#856404]",
  Cancelled: "bg-[#F8D7DA] text-[#721C24]",
};

const ENT_STATUS_COLORS = {
  "Available": "bg-[#E0E7FF] text-[#1E3A8A] border-[#1E3A8A]/30",
  "Used": "bg-[#D4EDDA] text-[#155724] border-[#155724]/30",
  "Expired": "bg-[#FFF3CD] text-[#856404] border-[#FFC107]/40",
  "Skipped and expired": "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/40",
  "Blocked": "bg-[#E2E8F0] text-[#495057] border-[#6C757D]/30",
  "Cancelled": "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/30",
};

const ALLOWED_ATT_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_ATT_SIZE = 10 * 1024 * 1024;

function fmtMZN(value) {
  if (value == null || isNaN(value)) return "—";
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "MZN", maximumFractionDigits: 0 }).format(value);
}

export default function MaintenancePlansPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [enums, setEnums] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [activeId, setActiveId] = useState(searchParams.get("open") || null);

  const load = async () => {
    const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
    const { data } = await api.get(`/maintenance-plans${q}`);
    setItems(data);
  };

  useEffect(() => { api.get("/enums").then((r) => setEnums(r.data)); }, []);
  useEffect(() => { load(); }, [statusFilter]);

  // Honor ?open=<plan-id> query parameter from MPM banner deep-link
  useEffect(() => {
    const open = searchParams.get("open");
    if (open) setActiveId(open);
  }, [searchParams]);

  const daysUntilExpiry = (vt) => {
    if (!vt) return null;
    return Math.round((new Date(vt).getTime() - Date.now()) / 86400000);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="mpm-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-[#1F3864]" />
            Maintenance Plans
          </h1>
          <p className="text-xs text-[#6C757D]">Pre-paid service plans (MPM) — entitlement tracking, skip authorisation and expiry forecast.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm text-sm flex items-center gap-1"
          data-testid="new-plan-btn"
        >
          <Plus className="w-3 h-3" /> New plan
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <label className="text-xs text-[#6C757D] uppercase tracking-widest">Status</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded-sm text-sm"
          data-testid="mpm-status-filter"
        >
          <option value="">All</option>
          <option>Active</option>
          <option>Expired</option>
          <option>Cancelled</option>
        </select>
        <div className="ml-auto text-xs text-[#6C757D]">{items.length} plan{items.length === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">Number</th>
              <th className="px-3 py-2 text-left">Customer</th>
              <th className="px-3 py-2 text-left">Vehicle</th>
              <th className="px-3 py-2 text-left">Plan</th>
              <th className="px-3 py-2 text-left">Brand</th>
              <th className="px-3 py-2 text-center">Consumed</th>
              <th className="px-3 py-2 text-right">Vehicle price</th>
              <th className="px-3 py-2 text-right">MPM price</th>
              <th className="px-3 py-2 text-left">Valid to</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Open</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="11" className="px-3 py-4 text-center text-xs text-[#6C757D]">No maintenance plans yet.</td></tr>
            )}
            {items.map((p) => {
              const days = daysUntilExpiry(p.valid_to);
              const expiringSoon = p.status === "Active" && days !== null && days >= 0 && days <= 90;
              return (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-[#F8FAFC] cursor-pointer" onClick={() => { setActiveId(p.id); setSearchParams({ open: p.id }); }} data-testid={`mpm-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-[#1F3864]">{p.number}</td>
                  <td className="px-3 py-2">{p.customer_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.vehicle_registration}</td>
                  <td className="px-3 py-2 text-xs">{p.plan_name}</td>
                  <td className="px-3 py-2 text-xs">{p.brand}</td>
                  <td className="px-3 py-2 text-center font-mono text-xs">{p.consumed_services}/{p.total_services}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmtMZN(p.vehicle_sale_price)}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmtMZN(p.mpm_package_price)}</td>
                  <td className="px-3 py-2 text-xs">
                    {p.valid_to}
                    {expiringSoon && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-[#856404] font-semibold"><AlertTriangle className="w-3 h-3" /> {days}d</span>}
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-2 text-right text-xs"><button className="text-[#1F3864] hover:underline" data-testid={`mpm-open-${p.id}`}>Open</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && enums && (
        <CreateDialog enums={enums} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />
      )}
      {activeId && (
        <DetailDrawer planId={activeId} onClose={() => { setActiveId(null); setSearchParams({}); }} onSaved={() => load()} />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[status] || "bg-[#E2E8F0] text-[#495057]"}`}>{status}</span>;
}

function EntStatusBadge({ status }) {
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-tight px-1.5 py-0.5 rounded-sm border ${ENT_STATUS_COLORS[status] || "bg-[#E2E8F0] text-[#495057] border-gray-300"}`}>{status}</span>;
}

// ============================================================
// CREATE DIALOG
// ============================================================
function CreateDialog({ enums, onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    vehicle_registration: "",
    vin: "",
    brand: enums.brands[0] || "",
    plan_name: "",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
    total_services: 5,
    vehicle_sale_price: "",
    mpm_package_price: "",
    notes: "",
  });
  const [approvalFile, setApprovalFile] = useState(null);
  const [approvalAttachmentId, setApprovalAttachmentId] = useState(null);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onFileChange = async (e) => {
    setErr("");
    const f = e.target.files[0];
    if (!f) return;
    if (!ALLOWED_ATT_TYPES.includes(f.type) && !/\.(pdf|jpe?g|png)$/i.test(f.name)) {
      setErr("Approval document must be PDF, JPG or PNG.");
      return;
    }
    if (f.size > MAX_ATT_SIZE) {
      setErr("File exceeds the 10 MB maximum.");
      return;
    }
    setApprovalFile(f);
    setUploadingAtt(true);
    try {
      const draftId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const fd = new FormData();
      fd.append("file", f);
      fd.append("record_type", "maintenance_plan_pending");
      fd.append("record_id", draftId);
      const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setApprovalAttachmentId(data.id);
    } catch (ex) {
      setErr(formatError(ex.response?.data?.detail));
      setApprovalFile(null);
    } finally { setUploadingAtt(false); }
  };

  const submit = async () => {
    setErr("");
    if (!approvalAttachmentId) { setErr("Approval document is required."); return; }
    setBusy(true);
    try {
      await api.post("/maintenance-plans", {
        ...form,
        total_services: Number(form.total_services),
        vehicle_sale_price: Number(form.vehicle_sale_price),
        mpm_package_price: Number(form.mpm_package_price),
        approval_attachment_id: approvalAttachmentId,
      });
      onSaved();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canSubmit = !!approvalAttachmentId && !!form.customer_name && !!form.vehicle_registration && !!form.plan_name && Number(form.vehicle_sale_price) >= 0 && Number(form.mpm_package_price) >= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mpm-create-dialog">
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-base font-bold">New maintenance plan</h2>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer name *"><input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} data-testid="mpm-customer-name" /></Field>
            <Field label="Customer phone"><input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className={inputCls} /></Field>
            <Field label="Vehicle plate *"><input value={form.vehicle_registration} onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value.toUpperCase() })} className={inputCls} data-testid="mpm-plate" /></Field>
            <Field label="VIN"><input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} className={inputCls} /></Field>
            <Field label="Brand *">
              <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} data-testid="mpm-brand">
                {enums.brands.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Plan name *"><input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} placeholder="e.g. BMW 5-year service" className={inputCls} data-testid="mpm-plan-name" /></Field>
            <Field label="Valid from *"><input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className={inputCls} /></Field>
            <Field label="Valid to *"><input type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} className={inputCls} /></Field>
            <Field label="Total services *"><input type="number" min="1" value={form.total_services} onChange={(e) => setForm({ ...form, total_services: e.target.value })} className={inputCls} data-testid="mpm-total" /></Field>
            <Field label="Vehicle sale price (MZN) *"><input type="number" min="0" step="0.01" value={form.vehicle_sale_price} onChange={(e) => setForm({ ...form, vehicle_sale_price: e.target.value })} className={inputCls} data-testid="mpm-vehicle-price" /></Field>
            <Field label="MPM package price (MZN) *"><input type="number" min="0" step="0.01" value={form.mpm_package_price} onChange={(e) => setForm({ ...form, mpm_package_price: e.target.value })} className={inputCls} data-testid="mpm-pkg-price" /></Field>
          </div>
          <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} /></Field>

          {/* Compulsory approval attachment */}
          <div className="border border-dashed border-[#DC3545]/60 bg-[#FFF5F5] rounded-md p-3" data-testid="mpm-approval-section">
            <div className="text-[10px] uppercase tracking-widest text-[#721C24] font-semibold mb-1.5 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Approval document — required
            </div>
            <div className="text-xs text-[#721C24] mb-2" data-testid="mpm-approval-help">
              Approval document required. The MPM plan cannot be saved without the signed approval document. PDF, JPG or PNG up to 10 MB.
            </div>
            {!approvalFile ? (
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={onFileChange}
                className="text-xs"
                data-testid="mpm-approval-upload"
              />
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-[#1F3864]" />
                <span className="font-medium" data-testid="mpm-approval-filename">{approvalFile.name}</span>
                <span className="text-[#6C757D]">({Math.round(approvalFile.size / 1024)} KB)</span>
                {uploadingAtt ? (
                  <span className="text-[#6C757D]">Uploading…</span>
                ) : approvalAttachmentId ? (
                  <span className="ml-auto inline-flex items-center gap-1 text-[#155724]" data-testid="mpm-approval-uploaded">
                    <Check className="w-3 h-3" /> Uploaded
                  </span>
                ) : null}
                <button type="button" onClick={() => { setApprovalFile(null); setApprovalAttachmentId(null); }} className="text-[#DC3545] hover:underline text-xs ml-2">Replace</button>
              </div>
            )}
          </div>

          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="mpm-error">{err}</div>}
        </div>
        <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !canSubmit}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="mpm-submit"
            title={!approvalAttachmentId ? "Upload the signed approval document first" : undefined}
          >
            {busy ? "Saving…" : "Create plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL DRAWER
// ============================================================
function DetailDrawer({ planId, onClose, onSaved }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Consume form
  const [consumeEnt, setConsumeEnt] = useState(null); // entitlement being consumed
  const [consumeForm, setConsumeForm] = useState({ service_case_id: "", alidata_job_card: "", mileage_km: "", approval_reason: "", notes: "" });
  const [skipDetails, setSkipDetails] = useState(null); // {skipped_entitlements:[{seq,label}]}
  const [cases, setCases] = useState([]);

  const reload = async () => {
    const { data } = await api.get(`/maintenance-plans/${planId}`);
    setPlan(data);
  };

  useEffect(() => {
    reload();
    api.get("/service-cases?open_only=false").then((r) => setCases(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  if (!plan) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
        <div className="bg-white w-full max-w-2xl h-full p-6 text-sm text-[#6C757D]" onClick={(e) => e.stopPropagation()}>Loading…</div>
      </div>
    );
  }

  const submitConsume = async () => {
    setErr("");
    if (!consumeEnt) return;
    setBusy(true);
    try {
      const payload = {
        sequence_number: consumeEnt.sequence_number,
        service_case_id: consumeForm.service_case_id || null,
        alidata_job_card: consumeForm.alidata_job_card || null,
        mileage_km: consumeForm.mileage_km ? Number(consumeForm.mileage_km) : null,
        approval_reason: consumeForm.approval_reason || null,
        notes: consumeForm.notes || "",
        confirm_skip: !!skipDetails,
      };
      await api.post(`/maintenance-plans/${planId}/consume`, payload);
      setConsumeEnt(null);
      setSkipDetails(null);
      setConsumeForm({ service_case_id: "", alidata_job_card: "", mileage_km: "", approval_reason: "", notes: "" });
      await reload();
      onSaved();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "skip_confirmation_required") {
        setSkipDetails(det);
      } else {
        setErr(formatError(det));
      }
    } finally { setBusy(false); }
  };

  const cancelPlan = async () => {
    const reason = prompt("Reason for cancelling this plan?");
    if (!reason) return;
    setBusy(true);
    try {
      await api.patch(`/maintenance-plans/${planId}`, { status: "Cancelled", comment: reason });
      await reload();
      onSaved();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remaining = plan.total_services - plan.consumed_services;
  const linkedCases = plan.linked_service_cases || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="mpm-detail-drawer">
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Maintenance plan</div>
            <div className="font-heading text-lg font-bold text-[#1A1A2E]">{plan.number}</div>
          </div>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Customer" value={plan.customer_name} />
            <Info label="Phone" value={plan.customer_phone} />
            <Info label="Vehicle" value={<span className="font-mono">{plan.vehicle_registration}</span>} />
            <Info label="Brand" value={plan.brand} />
            <Info label="Plan" value={plan.plan_name} />
            <Info label="Status" value={<StatusBadge status={plan.status} />} />
            <Info label="Valid from" value={plan.valid_from} />
            <Info label="Valid to" value={plan.valid_to} />
            <Info label="Vehicle sale price" value={<span data-testid="mpm-detail-vehicle-price">{fmtMZN(plan.vehicle_sale_price)}</span>} />
            <Info label="MPM package price" value={<span data-testid="mpm-detail-pkg-price">{fmtMZN(plan.mpm_package_price)}</span>} />
          </div>

          {/* Approval document */}
          {plan.approval_attachment_id && (
            <div className="border border-gray-200 rounded-md p-3 flex items-center gap-3" data-testid="mpm-detail-approval-doc">
              <FileText className="w-5 h-5 text-[#1F3864]" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Signed approval document</div>
                <div className="text-xs text-[#1A1A2E]">Permanent record — locked, cannot be deleted.</div>
              </div>
              <a
                href={`${api.defaults.baseURL}/attachments/${plan.approval_attachment_id}/download`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#1F3864] hover:underline"
                data-testid="mpm-detail-approval-download"
              >
                View / download <ExternalLink className="w-3 h-3" />
              </a>
              <Lock className="w-3.5 h-3.5 text-[#6C757D]" />
            </div>
          )}

          {/* Entitlements */}
          <div className="border border-gray-200 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Entitlements</div>
              <div className="text-xs font-mono">{plan.consumed_services} / {plan.total_services} used · {remaining} remaining</div>
            </div>
            <div className="w-full bg-[#F4F6F8] rounded-sm h-2 overflow-hidden border border-gray-200 mb-3">
              <div className="bg-[#1F3864] h-full" style={{ width: `${(plan.consumed_services / plan.total_services) * 100}%` }} />
            </div>
            <ul className="space-y-1.5">
              {(plan.entitlements || []).map((e) => (
                <li key={e.sequence_number} className="flex items-center gap-2 text-xs border border-gray-100 rounded-sm px-2 py-1.5" data-testid={`mpm-ent-${e.sequence_number}`}>
                  <span className="font-mono text-[#1F3864] font-bold w-6">#{e.sequence_number}</span>
                  <span className="flex-1 text-[#1A1A2E]">{e.label}</span>
                  {e.consumed_at && <span className="text-[#6C757D]">{fmtDateTime(e.consumed_at)}</span>}
                  {e.alidata_job_card && <span className="font-mono text-[10px] text-[#6C757D]">{e.alidata_job_card}</span>}
                  {e.mileage_km != null && <span className="font-mono text-[10px] text-[#6C757D]">{e.mileage_km} km</span>}
                  <EntStatusBadge status={e.status} />
                  {e.status === "Available" && plan.status === "Active" && (
                    <button
                      onClick={() => { setConsumeEnt(e); setSkipDetails(null); setErr(""); setConsumeForm({ service_case_id: "", alidata_job_card: "", mileage_km: "", approval_reason: "", notes: "" }); }}
                      className="text-[#1F3864] hover:underline text-xs"
                      data-testid={`mpm-consume-${e.sequence_number}`}
                    >
                      Consume
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Linked Service Cases */}
          <div className="border border-gray-200 rounded-md p-3" data-testid="mpm-linked-cases-section">
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">
              Linked service cases for {plan.vehicle_registration} ({linkedCases.length})
            </div>
            {linkedCases.length === 0 ? (
              <div className="text-xs text-[#6C757D]">No service cases on file for this vehicle.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-[#6C757D]">
                  <tr>
                    <th className="text-left py-1">Case #</th>
                    <th className="text-left py-1">Job card</th>
                    <th className="text-left py-1">Opened</th>
                    <th className="text-left py-1">Status</th>
                    <th className="text-left py-1">Advisor</th>
                    <th className="text-right py-1">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedCases.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100" data-testid={`mpm-linked-case-${c.id}`}>
                      <td className="py-1 font-mono text-[#1F3864]">{c.number}</td>
                      <td className="py-1 font-mono text-[10px]">{c.alidata_job_card || "—"}</td>
                      <td className="py-1">{fmtDateTime(c.opened_at)}</td>
                      <td className="py-1">{c.current_status}</td>
                      <td className="py-1">{c.service_advisor || "—"}</td>
                      <td className="py-1 text-right">
                        <Link to={`/service-cases/${c.id}`} className="text-[#1F3864] hover:underline">Open →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {plan.status === "Active" && (
            <button onClick={cancelPlan} className="text-xs text-[#DC3545] hover:underline" data-testid="mpm-cancel-btn">Cancel plan…</button>
          )}

          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="mpm-edit-error">{err}</div>}
          <div className="pt-3 border-t border-gray-100">
            <CommentThread recordType="maintenance_plan" recordId={planId} />
          </div>
        </div>
      </div>

      {/* Consume dialog */}
      {consumeEnt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setConsumeEnt(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="mpm-consume-dialog">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold">Consume entitlement #{consumeEnt.sequence_number}</h2>
              <button onClick={() => setConsumeEnt(null)}><X className="w-4 h-4 text-[#6C757D]" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-[#1A1A2E]">{consumeEnt.label}</div>

              {/* Skip warning */}
              {skipDetails && (
                <div className="border border-[#DC3545]/40 bg-[#FFF5F5] rounded-md p-3" data-testid="mpm-skip-warning">
                  <div className="flex items-start gap-2 text-[#721C24]">
                    <AlertTriangle className="w-4 h-4 mt-0.5" />
                    <div className="text-xs">
                      <div className="font-semibold mb-1">The following services have not been used:</div>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {skipDetails.skipped_entitlements.map((s) => (
                          <li key={s.sequence_number} data-testid={`mpm-skip-item-${s.sequence_number}`}>
                            <span className="font-mono">#{s.sequence_number}</span> {s.label}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-2 font-medium">
                        If you proceed, they will be <b>permanently expired</b>. This requires manager or supervisor authorisation.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Alidata job card *">
                  <input value={consumeForm.alidata_job_card} onChange={(e) => setConsumeForm({ ...consumeForm, alidata_job_card: e.target.value })} className={inputCls} data-testid="mpm-consume-jc" />
                </Field>
                <Field label="Mileage (km)">
                  <input type="number" value={consumeForm.mileage_km} onChange={(e) => setConsumeForm({ ...consumeForm, mileage_km: e.target.value })} className={inputCls} data-testid="mpm-consume-km" />
                </Field>
              </div>
              <Field label="Linked service case">
                <select value={consumeForm.service_case_id} onChange={(e) => setConsumeForm({ ...consumeForm, service_case_id: e.target.value })} className={inputCls}>
                  <option value="">(none)</option>
                  {cases.filter((c) => c.vehicle_registration === plan.vehicle_registration).map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
                </select>
              </Field>
              {skipDetails && (
                <Field label="Authorisation reason *">
                  <textarea value={consumeForm.approval_reason} onChange={(e) => setConsumeForm({ ...consumeForm, approval_reason: e.target.value })} rows={2} className={inputCls} data-testid="mpm-consume-reason" />
                </Field>
              )}
              <Field label="Notes">
                <textarea value={consumeForm.notes} onChange={(e) => setConsumeForm({ ...consumeForm, notes: e.target.value })} rows={2} className={inputCls} />
              </Field>
              {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="mpm-consume-error">{err}</div>}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setConsumeEnt(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancel</button>
              <button
                onClick={submitConsume}
                disabled={busy || (skipDetails && !consumeForm.approval_reason.trim()) || !consumeForm.alidata_job_card.trim()}
                className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60"
                data-testid="mpm-consume-submit"
              >
                {busy ? "Saving…" : (skipDetails ? "Authorise & consume" : "Confirm consumption")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="text-sm text-[#1A1A2E]">{value || "—"}</div>
    </div>
  );
}
